/**
 * 일정(연간시간표 칸) ↔ 학습내용(프로그램 표 행) 공통 짝짓기.
 *
 * 규칙(편제·프로젝트 공통, 고정 프로그램은 쓰지 않음):
 * - 학습내용 행은 스코프 안에서 order_index(→ created_at → id) 순서로 줄을 선다.
 * - 일정 칸도 날짜·교시 순서로 줄을 선다.
 * - i번째 일정 칸 ↔ i번째 학습내용 행. 내용이 모자라면 빈칸, 남으면 넘침(overflow).
 * - 셀 키(source_annual_cell_key)는 짝짓기에 쓰지 않는다. 시간표를 옮겨도 내용은 순서대로 따라간다.
 */

export type OrderedContentLike = {
  id?: string | null;
  order_index?: number | null;
  created_at?: string | null;
};

export function compareContentRowsByOrder(a: OrderedContentLike, b: OrderedContentLike): number {
  const ao = typeof a.order_index === 'number' && Number.isFinite(a.order_index) ? a.order_index : 0;
  const bo = typeof b.order_index === 'number' && Number.isFinite(b.order_index) ? b.order_index : 0;
  if (ao !== bo) return ao - bo;
  const ac = String(a.created_at ?? '');
  const bc = String(b.created_at ?? '');
  if (ac !== bc) return ac.localeCompare(bc);
  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
}

/** 원본 배열을 바꾸지 않고 정렬된 사본을 돌려준다 */
export function sortContentRowsByOrder<T extends OrderedContentLike>(rows: readonly T[]): T[] {
  return [...rows].sort(compareContentRowsByOrder);
}

export type ScheduleContentPair<S, C> = {
  slot: S;
  /** null = 이 일정 칸에 아직 학습내용 행이 없음(빈칸) */
  content: C | null;
  /** 스코프 안 위치(0부터). 내용이 없을 때 새 행을 만들 자리 */
  position: number;
};

export type ScheduleContentMatch<S, C> = {
  pairs: ScheduleContentPair<S, C>[];
  /** 일정 칸보다 많은 학습내용 행(순서대로) */
  overflow: C[];
  /** 정렬된 전체 학습내용 행 */
  sortedContents: C[];
};

/** 하나의 스코프(편제 한 열 / 프로젝트 하나)에 대한 일정 ↔ 학습내용 짝짓기 */
export function matchScheduleWithContent<S, C extends OrderedContentLike>(
  slots: readonly S[],
  contents: readonly C[]
): ScheduleContentMatch<S, C> {
  const sortedContents = sortContentRowsByOrder(contents);
  const pairs = slots.map((slot, position) => ({
    slot,
    content: sortedContents[position] ?? null,
    position,
  }));
  const overflow = sortedContents.slice(slots.length);
  return { pairs, overflow, sortedContents };
}

/** position 번째 학습내용 행(정렬 기준). 없으면 null */
export function contentAtSchedulePosition<C extends OrderedContentLike>(
  contents: readonly C[],
  position: number | null | undefined
): C | null {
  if (typeof position !== 'number' || !Number.isInteger(position) || position < 0) return null;
  return sortContentRowsByOrder(contents)[position] ?? null;
}

/** 정렬 기준 위치(0부터). 없으면 -1 */
export function positionOfContentRow<C extends OrderedContentLike>(contents: readonly C[], rowId: string): number {
  const id = rowId.trim().toLowerCase();
  if (!id) return -1;
  return sortContentRowsByOrder(contents).findIndex((row) => String(row.id ?? '').trim().toLowerCase() === id);
}

export type ScopedScheduleSlotRef = {
  annualCellKey: string;
  scopeType: 'track' | 'project';
  scopeKey: string;
};

type ScopedRowLike = OrderedContentLike & {
  scope_type: string;
  scope_key: string;
  source_annual_cell_key?: string | null;
};

const scopeGroupKey = (scopeType: string, scopeKey: string) => `${scopeType}\u0001${(scopeKey ?? '').trim()}`;

/**
 * 여러 스코프의 학습내용 행을 일정 칸에 짝지어, 화면용 사본의 `source_annual_cell_key`를
 * **지금 짝지어진 일정 칸 키**로 바꿔 돌려준다(넘침 행은 null). DB 값은 읽지도 쓰지도 않는다.
 * slots는 날짜·교시 순서여야 한다.
 */
export function projectScopedRowsOntoScheduleSlots<R extends ScopedRowLike>(
  slots: readonly ScopedScheduleSlotRef[],
  rows: readonly R[]
): R[] {
  const slotsByScope = new Map<string, ScopedScheduleSlotRef[]>();
  slots.forEach((slot) => {
    const key = scopeGroupKey(slot.scopeType, slot.scopeKey);
    const list = slotsByScope.get(key) ?? [];
    list.push(slot);
    slotsByScope.set(key, list);
  });
  const rowsByScope = new Map<string, R[]>();
  rows.forEach((row) => {
    if (row.scope_type !== 'track' && row.scope_type !== 'project') return;
    const key = scopeGroupKey(row.scope_type, row.scope_key);
    const list = rowsByScope.get(key) ?? [];
    list.push(row);
    rowsByScope.set(key, list);
  });
  const projectedKeyByRow = new Map<R, string | null>();
  rowsByScope.forEach((scopeRows, key) => {
    const { pairs, overflow } = matchScheduleWithContent(slotsByScope.get(key) ?? [], scopeRows);
    pairs.forEach((pair) => {
      if (pair.content) projectedKeyByRow.set(pair.content, pair.slot.annualCellKey);
    });
    overflow.forEach((row) => projectedKeyByRow.set(row, null));
  });
  return rows.map((row) => {
    if (!projectedKeyByRow.has(row)) return row;
    return { ...row, source_annual_cell_key: projectedKeyByRow.get(row) ?? null };
  });
}
